import { t } from '../i18nService';
import timeActions from '../../actions/TimeActions';
import uiActions from '../../actions/UIActions';
import apiReader from '../../store/readHelpers/ApiReader';
import sessionReader from '../../store/readHelpers/SessionReader';
import storeManager from '../../store/StoreManager';
import alertService from '../AlertService';
import apiService from '../api/ApiService';
import backgroundAudioService from '../BackgroundAudioService';
import deliverService from '../DeliveringService';
import marketplacePropertiesService from '../MarketplacePropertiesService';
import navigationService from '../NavigationService';
import pauseService from '../PauseService';
import routeTypeService from '../RouteTypeService';
import stateCacheService from '../StateCache';
import timeService from '../TimeService';
import utilService from '../UtilService';
import remainingTimeConstants from './RemainingTimeConstants';
import liveSessionHandler from './LiveSessionHandler';
import loginService from '../LoginService';

const remainingTimeHandler = {
    handleTimeRemainingResponse(assessmentRunId, response) {
        /*
         * Response from the apiService.fetchRemainingTime() can be:
         * {
         *  remainingTime: {int} // Time remaining in ms, or -1 if the test is open for re-delivery.
         *  durationUsed: {boolean} // Whether or not there is a duration (max time available) set for the test.
         *  assessmentDuration: {int || undefined} // The duration (max time available) for the test.
         *  pauseTime: {int} // Time stamp of the start of the pause.
         *  endTime: {int} // Time stamp of the end time for the candidate.
         * }
         */

        const {
            assessmentDuration,
            blockageReason,
            durationUsed,
            endTime,
            liveSessionState,
            pauseTime,
            remainingTime,
            remainingTimePrimaryEndTime,
        } = response;

        const state = storeManager.getState();
        const timeNow = utilService.getCurrentDate().getTime();
        const { time } = state;
        const { finalMinutesMode } = time;
        const isTestPaused = sessionReader.isPaused();
        const existingPauseTime = time.pauseTime;
        const existingEndTime = time.endTime;
        const pushChannels = sessionReader.getPushChannels();
        // As extra time is given continuously when you are paused with stop time, this should not alert the user in this case.
        const testIsOrWasRecentlyPausedWithStopTime = (pauseTime || existingPauseTime) && marketplacePropertiesService.isPauseWithStopTimeEnabled();

        loginService.handleStudentEviction(blockageReason);

        if (!testIsOrWasRecentlyPausedWithStopTime &&
            typeof pushChannels === 'object' && !!pushChannels.extraTime && remainingTime > 0 &&
            existingEndTime && existingEndTime > 0 && existingEndTime !== endTime &&
            !!this.lastUsedAssessmentRunId && this.lastUsedAssessmentRunId === assessmentRunId
        ) {
            this.handleEndTimeDifference(existingEndTime, endTime);
        }

        this.lastUsedAssessmentRunId = assessmentRunId;

        storeManager.dispatch(timeActions.setTimeRemainingFetchedFromServer(
            remainingTime,
            timeNow,
            remainingTime,
            timeNow,
            durationUsed,
            assessmentDuration,
            pauseTime,
            endTime,
            remainingTimePrimaryEndTime,
        ));

        this.handleTimeRemainingChange(remainingTime, time.timeRemaining, finalMinutesMode);
        liveSessionHandler.handleLiveSessionChange(liveSessionState);

        if (pauseTime && !isTestPaused) {
            pauseService.pause();
        } else if (!pauseTime && isTestPaused) {
            pauseService.resume();
        }

        return !!remainingTime;
    },

    handleEndTimeDifference(existingEndTime, newEndTime) {
        const minutes = Math.round((newEndTime - existingEndTime) / remainingTimeConstants.MINUTE_MILLISECONDS);
        if (minutes === 0) {
            // No end time change for candidate
            return;
        }
        const captionKey = minutes < 0 ? 'reduced' : 'extended';
        const minutesCaption = `${Math.abs(minutes)} ${t('generic.timeUnit.minute', { count: Math.abs(minutes) })}`;
        const messageBody = t(`messages.extraTime.${captionKey}`, { minutesCaption });

        const message = {
            body: messageBody,
        };
        alertService.showMessage(message);
    },

    handleTimeRemainingChange(timeRemaining, previousTimeRemaining, finalMinutesMode) {
        stateCacheService.getStateCacheInstance().setTimeRemaining(timeRemaining);

        // for now nothing to do when open for redelivery
        if (timeRemaining === -1) {
            return;
        }

        if (timeRemaining === 0 && previousTimeRemaining !== 0) {
            this.handleTimeout();
        } else if (!finalMinutesMode && timeRemaining < remainingTimeConstants.FINAL_MINUTES_MODE_MILLISECONDS) {
            timeService.switchOnFinalMinutesMode();
        } else if (finalMinutesMode && timeRemaining > remainingTimeConstants.FINAL_MINUTES_MODE_MILLISECONDS) {
            timeService.switchOffFinalMinutesMode();
        }
    },

    async handleTimeout() {
        const { currentlyShown } = storeManager.getState();

        /**
         * Normal behavior is to redirect to delivering when time runs out. However, it would be unfortunate to be
         * taken away while in the process of adding extra time, delivering to file, exiting secure browser,
         * withdrawing or delivering blank.
         */

        if (!routeTypeService.isTimeoutSafe(currentlyShown.route) && !this.timeOutHandlingInProgress) {
            this.timeOutHandlingInProgress = true;
            // set the overlay to false to not show it when end time passes
            storeManager.dispatch(uiActions.setUseBackgroundAudioOverlay(false));

            if (sessionReader.isAutoDeliverHighStakesEnabled()) {
                // Cancel GetRemainingTime intervals. They are started again with timeService.start when switching context
                timeService.cancelSchedules();
                const assessmentRunId = sessionReader.getAssessmentRunId();

                // Check connectivity prior to auto-delivery
                try {
                    // Call ApiService directly to avoid response being handled in TimeService
                    await apiService.fetchRemainingTime(assessmentRunId, false);
                } catch (e) {
                    console.log('Offline on delivery');
                }

                if (apiReader.isOnline()) {
                    navigationService.navigateToAutoSubmit();

                    // 1s delays is needed to guarantee that user response storing is initialized BEFORE test delivering
                    await utilService.delay(1000);
                    await deliverService.clientSideAutoDeliver(assessmentRunId);
                } else {
                    if (backgroundAudioService.hasBackgroundAudio(assessmentRunId)) {
                        backgroundAudioService.stopAndRemoveAudio();
                    }
                    timeService.updateTimeRemainingFromCache();
                    deliverService.clientSideOfflineDeliver(assessmentRunId);
                }
            } else if (sessionReader.isParentTestContextOutsideChildTest()) {
                navigationService.navigateToTestOverview();
            } else {
                navigationService.navigateToDeliveringPage();
            }

            this.timeOutHandlingInProgress = false;
        }

        timeService.reportTimeoutWarningToMonitor();
    },
};

export default remainingTimeHandler;
